import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { useCreateLocale } from "./use-locales";
import { LOCALE_CODE_MAX, LOCALE_DISPLAY_NAME_MAX } from "../shared/compliance-types";
import { canonicaliseBcp47, isValidBcp47 } from "@/shared/lib/bcp47";
import { resolveComplianceError } from "../shared/resolve-compliance-error";

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function CreateLocaleDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isCanonical, setIsCanonical] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateLocale();

  useEffect(() => {
    if (open) {
      setCode("");
      setDisplayName("");
      setIsCanonical(false);
      setError(null);
    }
  }, [open]);

  // Canonicalise on blur so the operator sees the shape backend stores
  // ("en-US"), not the keystroke shape ("en-us").
  const handleCodeBlur = () => {
    if (code.length > 0) {
      setCode(canonicaliseBcp47(code));
    }
  };

  const trimmedDisplayName = displayName.trim();
  const isValid =
    isValidBcp47(code) &&
    code.length <= LOCALE_CODE_MAX &&
    trimmedDisplayName.length > 0 &&
    trimmedDisplayName.length <= LOCALE_DISPLAY_NAME_MAX;

  const handleSubmit = async () => {
    if (!isValid || create.isPending) return;
    setError(null);
    try {
      await create.mutateAsync({
        code,
        displayName: trimmedDisplayName,
        isCanonical: isCanonical || undefined,
      });
      onOpenChange(false);
    } catch (err) {
      setError(resolveComplianceError(err, t));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !create.isPending && onOpenChange(next)}>
      <DialogContent data-testid="compliance-create-locale-dialog">
        <DialogHeader>
          <DialogTitle>{t("compliance.locales.create.title")}</DialogTitle>
          <DialogDescription>{t("compliance.locales.create.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="locale-code">{t("compliance.locales.fields.code")}</Label>
            <Input
              id="locale-code"
              data-testid="compliance-locale-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onBlur={handleCodeBlur}
              maxLength={LOCALE_CODE_MAX}
              placeholder="es-AR"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              {t("compliance.locales.fields.codeHint")}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="locale-display-name">
              {t("compliance.locales.fields.displayName")}
            </Label>
            <Input
              id="locale-display-name"
              data-testid="compliance-locale-display-name-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={LOCALE_DISPLAY_NAME_MAX}
              placeholder="Español (Argentina)"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="locale-canonical"
              type="checkbox"
              data-testid="compliance-locale-canonical-input"
              checked={isCanonical}
              onChange={(e) => setIsCanonical(e.target.checked)}
              className="h-4 w-4"
            />
            <Label htmlFor="locale-canonical">{t("compliance.locales.fields.isCanonical")}</Label>
          </div>
          {isCanonical && (
            <p className="text-xs text-muted-foreground">
              {t("compliance.locales.fields.isCanonicalHint")}
            </p>
          )}

          {error && (
            <p data-testid="compliance-create-locale-error" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            {t("compliance.actions.cancel")}
          </Button>
          <Button
            data-testid="compliance-create-locale-submit"
            onClick={handleSubmit}
            disabled={!isValid || create.isPending}
          >
            {create.isPending ? t("compliance.actions.saving") : t("compliance.actions.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
